import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import App from './App';
import { AuthProvider } from './lib/auth';
import ImpersonationBanner from './components/ImpersonationBanner';
import { setupPWA } from './lib/pwa';
import { getApiBase } from './lib/api';
import './index.css';

function bootApp() {
  setupPWA();

  // Keep Render free-tier backend warm while the tab is open. /health is a
  // 1ms endpoint — this prevents the ~30s cold-start that otherwise hits the
  // first scan after 15 min of idle.
  //
  // Use getApiBase() instead of import.meta.env.VITE_API_URL directly so we
  // respect the same host-aware resolution as every other API call. When the
  // page is served via ngrok, API resolves to '' (relative) — and there's
  // nothing meaningful to keep warm on a local dev backend, so we skip.
  const API = getApiBase();
  if (API && /^https?:/.test(API)) {
    const ping = () => fetch(`${API}/health`, { credentials: 'omit' }).catch(() => {});
    ping();
    setInterval(ping, 4 * 60_000); // every 4 min (Render sleeps at 15 min)
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Re-fetch when the user comes back to the tab so the lists always
        // reflect what other users / the tech app did while they were away —
        // no manual refresh required.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // 30s: moving between pages re-uses what was just loaded instead of
        // re-downloading every list (the 10s window re-pulled the big lists on
        // nearly every navigation, which is what saturated the DB link). Saves
        // still show at once — mutations invalidate their own queries — and the
        // backend caches clear on every write.
        staleTime: 30_000,
        retry: 1,
      },
    },
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <ImpersonationBanner />
            <App />
            {/* DS §Feedback/.toast — surface card, hairline border, elevation 4,
                Manrope copy, and status colours drawn from the theme tokens. */}
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-md)',
                  boxShadow: 'var(--shadow-4)',
                  fontFamily: "'Manrope', system-ui, sans-serif",
                  fontSize: '12.5px',
                  fontWeight: 600,
                  padding: '13px 15px',
                },
                success: { iconTheme: { primary: 'var(--success)', secondary: 'var(--surface)' } },
                error: { iconTheme: { primary: 'var(--error)', secondary: 'var(--surface)' } },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

// ── Stale-service-worker self-heal ──────────────────────────────────────────
// Backend routes like /api/pos/view-via-link render server-side HTML (CEO
// approval pages, file/PDF opens). If an OLD service worker — one built before
// the workbox `navigateFallbackDenylist: [/^\/api\//]` — is still installed in
// a logged-in browser, it mis-serves the SPA app-shell for that navigation,
// and React Router (not knowing the path) bounces the user to the dashboard.
// If we ever detect the SPA itself booting at an /api/* URL, that IS the bug:
// unregister the service worker(s) and reload so the request reaches the
// backend over the network. A one-shot sessionStorage flag prevents a reload
// loop; it's cleared on any normal (non-/api) boot. This runs BEFORE React
// mounts, so the router never gets the chance to redirect.
const isApiMisroute =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/api/');

if (isApiMisroute) {
  const HEAL_KEY = '_api_swheal';
  let alreadyTried = false;
  try { alreadyTried = sessionStorage.getItem(HEAL_KEY) === '1'; } catch { /* private mode */ }
  if (!alreadyTried) {
    try { sessionStorage.setItem(HEAL_KEY, '1'); } catch { /* ignore */ }
    (async () => {
      try {
        const regs = (await navigator.serviceWorker?.getRegistrations?.()) || [];
        await Promise.all(regs.map((r) => r.unregister()));
      } catch { /* ignore */ }
      window.location.reload();
    })();
  }
  // Intentionally do NOT mount the SPA at an /api/* URL — the reload above (or
  // an already-healed SW) lets nginx route it straight to the backend page.
} else {
  try { sessionStorage.removeItem('_api_swheal'); } catch { /* ignore */ }
  bootApp();
}
